import type { BadgeColors } from "@/components/base/badges/badge-types";
import type { AppTranslateFn } from "@/hooks/use-app-translation";

export type ConfigurationTypeTableAccessDisplay = {
	label: string;
	color: BadgeColors;
};

export type ConfigurationTypeTableAccessKeys = {
	available: string;
	notAvailable: string;
	noSubOrganizations: string;
	allSubOrganizations: string;
	partial: string;
	tooltipNone: string;
};

/**
 * Org-admin configuration table Access column — five display states by org category
 * (direct customer vs service provider) and grant/enabled flags.
 */
export function getConfigurationTypeTableAccessDisplay(input: {
	enabled?: boolean;
	grantedCount: number;
	totalSubOrganizations: number;
	keys: ConfigurationTypeTableAccessKeys;
	t: AppTranslateFn;
}): ConfigurationTypeTableAccessDisplay {
	const { enabled, grantedCount, totalSubOrganizations, keys, t } = input;

	if (totalSubOrganizations <= 0) {
		if (enabled === false) {
			return {
				label: t(keys.notAvailable),
				color: "gray",
			};
		}
		return {
			label: t(keys.available),
			color: "success",
		};
	}

	if (grantedCount === 0) {
		return {
			label: t(keys.noSubOrganizations),
			color: "gray",
		};
	}
	if (grantedCount >= totalSubOrganizations) {
		return {
			label: t(keys.allSubOrganizations),
			color: "success",
		};
	}
	return {
		label: t(keys.partial, {
			granted: grantedCount,
			total: totalSubOrganizations,
		}),
		color: "blue",
	};
}

/** Hover text for Access column when the org has sub-organizations. */
export function getConfigurationTypeTableAccessTooltip(input: {
	grantedSubOrganizations: ReadonlyArray<{ name?: string | null }>;
	keys: Pick<ConfigurationTypeTableAccessKeys, "tooltipNone">;
	t: AppTranslateFn;
}): string {
	const names = input.grantedSubOrganizations
		.map((subOrg) => subOrg.name?.trim())
		.filter((name): name is string => Boolean(name))
		.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

	if (names.length === 0) {
		return input.t(input.keys.tooltipNone);
	}

	return names.join(", ");
}
